/**
 * Re-consent banner — Sprint BO.
 *
 * Renders above the main shell when the logged-in user's
 * `pdn_consent_version` doesn't match the current policy. Two
 * actions: accept inline, or jump to /privacy for the full text.
 *
 * Why a banner instead of a modal: a modal that blocks the app
 * the moment a new policy ships interrupts a user mid-task who
 * may be on a critical-path operation (approving a deal,
 * publishing a listing). The banner is persistent + dismiss-
 * resistant (no close X) without freezing the workspace. Hard
 * gating can come later if regulators ask.
 */

import { useEffect, useState } from "react";
import { BadgeCheck, FileText, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@vseh/ui";

import { meApi, type MeDto } from "../api";
import { useT } from "../i18n";


export function ConsentRefreshBanner(props: {
  onNavigate: (path: string) => void;
}): JSX.Element | null {
  const t = useT();
  const [me,   setMe]   = useState<MeDto | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    meApi.get()
      .then((m) => { if (alive) setMe(m); })
      .catch(() => { /* banner is best-effort; ignore */ });
    return () => { alive = false; };
  }, []);

  if (me === null || !me.needs_consent_refresh) return null;

  async function accept() {
    setBusy(true);
    try {
      await meApi.acceptConsent();
      setMe(null);    // hide the banner
    } catch {
      // Soft-fail; user can use the Privacy page if this errors.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-warning-200 bg-warning-50 px-4 py-2 text-xs text-warning-900">
      <ShieldAlert className="size-4 shrink-0" />
      <span className="flex-1 min-w-[12rem]">
        {t("privacy.banner.text")}
      </span>
      <Button
        variant="ghost" size="xs"
        leftIcon={<FileText className="size-3.5" />}
        onClick={() => props.onNavigate("/privacy")}
      >
        {t("privacy.banner.read")}
      </Button>
      <Button
        variant="primary" size="xs"
        leftIcon={busy
          ? <Loader2 className="size-3.5 animate-spin" />
          : <BadgeCheck className="size-3.5" />}
        disabled={busy}
        onClick={accept}
      >
        {t("privacy.banner.accept")}
      </Button>
    </div>
  );
}
